import { useState, memo } from 'react';
import { useMarket } from '@contexts/MarketContext';
import { cn } from '@lib/utils';
import { CashFlowReport } from '@components/reports/CashFlowReport';
import { PLAnalysisTable } from '@components/investor-demo/PLAnalysisTable';

/**
 * Reports Page Component
 * @task B.INV.9 - Reports page with tabbed navigation
 *
 * Features tabbed navigation like reference design:
 * - DRE (P&L Statement)
 * - Fluxo de Caixa (Cash Flow)
 * - Vencimentos (Due Dates)
 * - Inadimplencia (Delinquency)
 * - Despesas (Expenses)
 * - Reembolsos (Reimbursements)
 */

interface ReportTab {
  id: string;
  label: string;
  labelBR: string;
  icon: JSX.Element;
  available: boolean;
}

const reportTabs: ReportTab[] = [
  {
    id: 'dre',
    label: 'P&L Statement',
    labelBR: 'DRE',
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    available: true,
  },
  {
    id: 'cash-flow',
    label: 'Cash Flow',
    labelBR: 'Fluxo de Caixa',
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    available: true,
  },
  {
    id: 'due-dates',
    label: 'Due Dates',
    labelBR: 'Vencimentos',
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    available: false,
  },
  {
    id: 'delinquency',
    label: 'Delinquency',
    labelBR: 'Inadimplência',
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    available: false,
  },
  {
    id: 'expenses',
    label: 'Expenses',
    labelBR: 'Despesas',
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    available: false,
  },
  {
    id: 'reimbursements',
    label: 'Reimbursements',
    labelBR: 'Reembolsos',
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
      </svg>
    ),
    available: false,
  },
];

export function ReportsPage(): JSX.Element {
  const { isBrazil, formatCurrency } = useMarket();
  const [activeTab, setActiveTab] = useState('cash-flow');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: '',
  });

  // Summary metrics aligned with CashFlowReport data
  // US Inflows: Recebimento(969K) + Novas Vendas(356K) + Receitas Fin(9.5K) + Emprestimos(100K) = 1,434,500
  // US Outflows: OPEX(494K) + CAPEX(208K) + Financing(63.5K) = 765,550
  // Net: 1,434,500 - 765,550 = 668,950
  const summaryMetrics = isBrazil
    ? {
        totalInflows: 7172500,  // 1,434,500 * 5
        totalOutflows: 3827750, // 765,550 * 5
        netBalance: 3344750,    // 668,950 * 5
      }
    : {
        totalInflows: 1434500,
        totalOutflows: 765550,
        netBalance: 668950,
      };

  return (
    <div className="space-y-4 sm:space-y-6 bg-gradient-to-b from-finova-teal-50/30 to-transparent p-4 sm:p-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">
            {isBrazil ? 'Relatórios' : 'Reports'}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {isBrazil ? 'Análises financeiras e relatórios gerenciais' : 'Financial analysis and management reports'}
          </p>
        </div>

        {/* Period Selector & Actions */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm">
            <span className="text-xs font-medium text-gray-500">
              {isBrazil ? 'Período' : 'Period'}
            </span>
            <select className="text-sm font-medium text-gray-700 bg-transparent border-none focus:ring-0 cursor-pointer">
              <option>{isBrazil ? 'Mensal' : 'Monthly'}</option>
              <option>{isBrazil ? 'Trimestral' : 'Quarterly'}</option>
              <option>{isBrazil ? 'Anual' : 'Annual'}</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-finova-teal-500 focus:ring-1 focus:ring-finova-teal-500"
              placeholder={isBrazil ? 'Data Início' : 'Start Date'}
            />
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-finova-teal-500 focus:ring-1 focus:ring-finova-teal-500"
              placeholder={isBrazil ? 'Data Fim' : 'End Date'}
            />
          </div>
          <div className="flex items-center gap-2">
            <button className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 shadow-sm hover:bg-gray-50 transition-colors">
              <FilterIcon className="h-4 w-4" />
              <span className="hidden sm:inline">{isBrazil ? 'Filtros' : 'Filters'}</span>
            </button>
            <button className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 shadow-sm hover:bg-gray-50 transition-colors">
              <DownloadIcon className="h-4 w-4" />
              <span className="hidden sm:inline">{isBrazil ? 'Exportar' : 'Export'}</span>
            </button>
            <button className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 shadow-sm hover:bg-gray-50 transition-colors">
              <PrinterIcon className="h-4 w-4" />
              <span className="hidden sm:inline">{isBrazil ? 'Imprimir' : 'Print'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="rounded-xl bg-white shadow-card">
        {/* Tab Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-2 sm:px-4">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-1">
            {reportTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => tab.available && setActiveTab(tab.id)}
                disabled={!tab.available}
                className={cn(
                  'relative flex items-center gap-2 px-3 sm:px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors',
                  activeTab === tab.id
                    ? 'text-finova-teal-600'
                    : tab.available
                      ? 'text-gray-500 hover:text-gray-700'
                      : 'text-gray-300 cursor-not-allowed'
                )}
              >
                {tab.icon}
                <span>{isBrazil ? tab.labelBR : tab.label}</span>
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-finova-teal-500 rounded-full" />
                )}
                {!tab.available && (
                  <span className="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-400">
                    {isBrazil ? 'Em breve' : 'Soon'}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Real-time indicator */}
          <div className="hidden sm:flex items-center gap-2 px-3 py-2">
            <div className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1">
              <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-medium text-emerald-600">
                {isBrazil ? 'Atualização em tempo real' : 'Real-time update'}
              </span>
            </div>
            <button className="text-xs font-medium text-finova-teal-600 hover:text-finova-teal-700 transition-colors">
              {isBrazil ? 'Atualizar' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Summary Bar */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 border-b border-gray-100 px-2 sm:px-6 py-3 sm:py-4 bg-gray-50/50">
          <SummaryItem
            label={isBrazil ? 'Total Entradas' : 'Total Inflows'}
            value={summaryMetrics.totalInflows}
            formatCurrency={formatCurrency}
            trend="up"
            color="emerald"
            isBrazil={isBrazil}
          />
          <SummaryItem
            label={isBrazil ? 'Total Saídas' : 'Total Outflows'}
            value={summaryMetrics.totalOutflows}
            formatCurrency={formatCurrency}
            trend="down"
            color="rose"
            isNegative
            isBrazil={isBrazil}
          />
          <SummaryItem
            label={isBrazil ? 'Saldo Acumulado' : 'Net Balance'}
            value={summaryMetrics.netBalance}
            formatCurrency={formatCurrency}
            trend="up"
            color="teal"
            isBrazil={isBrazil}
          />
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'cash-flow' && <CashFlowReport />}
        {activeTab === 'dre' && <PLAnalysisTable className="mt-0" />}
        {activeTab === 'due-dates' && <ComingSoonPlaceholder isBrazil={isBrazil} feature="Vencimentos" featureEN="Due Dates" />}
        {activeTab === 'delinquency' && <ComingSoonPlaceholder isBrazil={isBrazil} feature="Inadimplência" featureEN="Delinquency" />}
        {activeTab === 'expenses' && <ComingSoonPlaceholder isBrazil={isBrazil} feature="Despesas" featureEN="Expenses" />}
        {activeTab === 'reimbursements' && <ComingSoonPlaceholder isBrazil={isBrazil} feature="Reembolsos" featureEN="Reimbursements" />}
      </div>
    </div>
  );
}

// Summary Item Component
interface SummaryItemProps {
  label: string;
  value: number;
  formatCurrency: (amount: number) => string;
  trend: 'up' | 'down';
  color: 'emerald' | 'rose' | 'teal';
  isNegative?: boolean;
  isBrazil?: boolean;
}

// Compact currency formatter for mobile
const formatCompactCurrency = (amount: number, isBrazil: boolean): string => {
  const absValue = Math.abs(amount);
  if (isBrazil) {
    if (absValue >= 1000000) return `R$${(absValue / 1000000).toFixed(1)}M`;
    if (absValue >= 1000) return `R$${(absValue / 1000).toFixed(0)}K`;
    return `R$${absValue.toFixed(0)}`;
  }
  if (absValue >= 1000000) return `$${(absValue / 1000000).toFixed(1)}M`;
  if (absValue >= 1000) return `$${(absValue / 1000).toFixed(0)}K`;
  return `$${absValue.toFixed(0)}`;
};

const SummaryItem = memo(function SummaryItem({
  label,
  value,
  formatCurrency,
  trend,
  color,
  isNegative,
  isBrazil = false,
}: SummaryItemProps): JSX.Element {
  const colorClasses = {
    emerald: 'text-emerald-600',
    rose: 'text-rose-600',
    teal: 'text-finova-teal-600',
  };

  const bgClasses = {
    emerald: 'bg-emerald-50',
    rose: 'bg-rose-50',
    teal: 'bg-finova-teal-50',
  };

  const fullValue = `${isNegative ? '-' : ''}${formatCurrency(value)}`;
  const compactValue = `${isNegative ? '-' : ''}${formatCompactCurrency(value, isBrazil)}`;

  return (
    <div className={cn('rounded-lg p-3 sm:p-4 overflow-hidden', bgClasses[color])}>
      <p className="text-[10px] sm:text-xs font-medium text-gray-500 truncate">{label}</p>
      <div className="mt-1 flex items-center gap-1 sm:gap-2">
        <span className={cn('text-sm sm:text-xl font-bold truncate', colorClasses[color])} title={fullValue}>
          <span className="sm:hidden">{compactValue}</span>
          <span className="hidden sm:inline">{fullValue}</span>
        </span>
        <span className={cn(
          'text-[10px] sm:text-xs font-medium flex-shrink-0',
          trend === 'up' ? 'text-emerald-600' : 'text-rose-600'
        )}>
          {trend === 'up' ? '↑' : '↓'}
        </span>
      </div>
    </div>
  );
});

// Coming Soon Placeholder
interface ComingSoonPlaceholderProps {
  isBrazil: boolean;
  feature: string;
  featureEN: string;
}

const ComingSoonPlaceholder = memo(function ComingSoonPlaceholder({
  isBrazil,
  feature,
  featureEN,
}: ComingSoonPlaceholderProps): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl bg-white p-12 sm:p-16 shadow-card">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
        <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h3 className="mt-4 text-lg font-semibold text-gray-900">
        {isBrazil ? `${feature} - Em Desenvolvimento` : `${featureEN} - Coming Soon`}
      </h3>
      <p className="mt-2 text-center text-sm text-gray-500 max-w-md">
        {isBrazil
          ? 'Este relatório está sendo desenvolvido e estará disponível em breve. Fique atento às atualizações!'
          : 'This report is being developed and will be available soon. Stay tuned for updates!'}
      </p>
    </div>
  );
});

// Icons
function FilterIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
    </svg>
  );
}

function DownloadIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}

function PrinterIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
    </svg>
  );
}

export default ReportsPage;
